/**
 * Name Formatter Utility
 * 
 * Standardizes name display across the application.
 * Format: "LASTNAME, First Middle" (e.g., "CHEN, Emily Grace")
 */

/**
 * Capitalize the first letter of each word (space/hyphen/apostrophe
 * separated) while PRESERVING interior casing so "McKenna", "DiCaprio"
 * survive. "emily grace" → "Emily Grace", "mary-jane" → "Mary-Jane",
 * "o'brien" → "O'Brien".
 */
export const capitalizeNameWords = (str) => {
  let s = str || '';
  // Whole-string ALL CAPS (e.g. "EMILY GRACE") → title case; short
  // tokens like initials ("MJ") are left alone.
  if (s.length > 2 && s === s.toUpperCase() && /[A-Z]/.test(s)) s = s.toLowerCase();
  return s.replace(/(^|[\s\-'])([a-z])/g, (m, sep, ch) => sep + ch.toUpperCase());
};

/**
 * Format a raw name STRING ("First Last" or "Last, First") with the
 * standard casing (LASTNAME uppercase, first name capitalized) in the
 * requested order. Used by list surfaces that only have a pre-built
 * client_name string plus a first/last order toggle.
 */
export const formatClientNameString = (name, order = 'last_first') => {
  if (!name || typeof name !== 'string') return name || '';
  const trimmed = name.trim();
  if (!trimmed) return '';
  let first = '';
  let last = '';
  if (trimmed.includes(',')) {
    const [l, f] = trimmed.split(',').map((s) => s.trim());
    last = l;
    first = f || '';
  } else {
    const parts = trimmed.split(/\s+/);
    if (parts.length < 2) return trimmed.toUpperCase();
    last = parts.pop();
    first = parts.join(' ');
  }
  const L = last.toUpperCase();
  const F = capitalizeNameWords(first);
  if (!F) return L;
  return order === 'first_last' ? `${F} ${L}` : `${L}, ${F}`;
};

/**
 * Format a client OBJECT with the standard casing in the requested
 * order ("last_first" → "CHEN, Emily" | "first_last" → "Emily CHEN").
 */
export const formatClientNameOrdered = (client, order = 'last_first') => {
  if (!client) return '';
  const middle = (client.middle_name || '').trim();
  const rawFirst = [(client.first_name || '').trim(), middle].filter(Boolean).join(' ');
  const first = capitalizeNameWords(rawFirst);
  const last = (client.last_name || '').trim().toUpperCase();
  if (first || last) {
    if (!last) return first;
    if (!first) return last;
    return order === 'first_last' ? `${first} ${last}` : `${last}, ${first}`;
  }
  return client.full_name ? formatClientNameString(client.full_name, order) : '';
};

/**
 * Format a name for display in "LASTNAME, First Middle" format
 * @param {string} firstName - First name (may include middle name)
 * @param {string} lastName - Last name
 * @returns {string} Formatted name like "CHEN, Emily Grace"
 */
export const formatDisplayName = (firstName, lastName) => {
  const first = capitalizeNameWords((firstName || '').trim());
  const last = (lastName || '').trim();
  
  if (!first && !last) return 'Unknown';
  if (!last) return first;
  if (!first) return last.toUpperCase();
  
  return `${last.toUpperCase()}, ${first}`;
};

/**
 * Format a full name string for display
 * Attempts to parse "First Last" or "First Middle Last" format
 * @param {string} fullName - Full name string
 * @returns {string} Formatted name like "LASTNAME, First Middle"
 */
export const formatFullName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return 'Unknown';
  
  const trimmed = fullName.trim();
  if (!trimmed) return 'Unknown';
  
  const parts = trimmed.split(/\s+/);
  
  if (parts.length === 1) {
    // Single name - treat as last name
    return parts[0].toUpperCase();
  }
  
  // Last part is last name, rest is first/middle
  const lastName = parts[parts.length - 1];
  const firstName = parts.slice(0, -1).join(' ');
  
  return `${lastName.toUpperCase()}, ${capitalizeNameWords(firstName)}`;
};

/**
 * Format a client object for display
 * @param {Object} client - Client object with first_name, last_name, or full_name
 * @returns {string} Formatted name like "CHEN, Emily Grace" or
 *                    "CHEN, Emily Grace (Em)" when a preferred name differs.
 */
export const formatClientName = (client) => {
  if (!client) return 'Unknown';

  let base = 'Unknown';

  // If we have separate first/last names, use them
  if (client.last_name || client.first_name) {
    base = formatDisplayName(client.first_name, client.last_name);
  } else if (client.full_name) {
    // Fall back to full_name
    base = formatFullName(client.full_name);
  } else if (client.name) {
    // Last resort - check for name field
    base = formatFullName(client.name);
  }

  // Append preferred name when it adds information (i.e. not the same as
  // the legal first name).  Mirrors the legacy ``formatClientName`` in
  // App.js: "LASTNAME, First (Preferred)".
  const preferred = (client.preferred_name || '').trim();
  if (preferred) {
    const first = (client.first_name || '').trim();
    if (!first || preferred.toLowerCase() !== first.toLowerCase()) {
      // Avoid duplicating the preferred when ``base`` already contains it.
      if (!base.toLowerCase().includes(`(${preferred.toLowerCase()})`)) {
        return `${base} (${capitalizeNameWords(preferred)})`;
      }
    }
  }

  return base;
};

/**
 * Format a name from a note or appointment object
 * These often store client_name as a pre-formatted string
 * @param {string} clientName - Client name string from note/appointment
 * @param {Object} clientData - Optional client data for better formatting
 * @returns {string} Formatted name
 */
export const formatStoredClientName = (clientName, clientData = null) => {
  // If we have client data, prefer that for accurate formatting
  if (clientData && (clientData.first_name || clientData.last_name)) {
    return formatDisplayName(clientData.first_name, clientData.last_name);
  }
  
  // Check if already in "LASTNAME, First" format
  if (clientName && clientName.includes(',')) {
    const [last, first] = clientName.split(',').map(s => s.trim());
    // Re-format to ensure consistent casing
    return `${last.toUpperCase()}, ${capitalizeNameWords(first)}`;
  }
  
  // Parse as full name
  return formatFullName(clientName);
};

/**
 * Sort key for client lists — matches the "LASTNAME, First" display
 * order used by formatClientName so dropdowns read alphabetically.
 */
export const clientSortKey = (c) => {
  const full = (c?.full_name || '').trim();
  const last = (c?.last_name || '').trim() || (full.split(' ').slice(-1)[0] || '');
  const first = (c?.first_name || '').trim() || full.split(' ').slice(0, -1).join(' ');
  return `${last}, ${first}`.toLowerCase();
};

export const sortClientsByName = (list) =>
  [...(list || [])].sort((a, b) => clientSortKey(a).localeCompare(clientSortKey(b)));

export default {
  formatDisplayName,
  formatFullName,
  formatClientName,
  formatStoredClientName
};
